import {
  createRequestId,
  getRequestOrganizationId,
} from '@api/request-correlation'

import {
  createSanitizedException,
  sanitizeCustomProperties,
  sanitizeErrorDetails,
  sanitizeLogString,
  sanitizeLogValue,
} from './log-sanitizer'
import { telemetryClient } from './log-transport'

export const LOG_SCHEMA_VERSION = '1.0'
export const FRONTEND_SERVICE = 'frontend'
export const PAYLOAD_MAX_LENGTH = 7500
export const SEQUENCE_SCOPE_LIMIT = 1000

const ORPHAN_SEQUENCE_KEY = '__orphan__'
const LOGGER_RUN_ID = createRequestId()

const severityByLevel = {
  trace: 0,
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  critical: 4,
}

const sequenceByScope = new Map()

const isObject = (value) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const shouldOmit = (value) =>
  value === undefined || value === null || value === ''

const pruneValue = (value) => {
  if (Array.isArray(value)) {
    const nextValue = value.map(pruneValue).filter((item) => !shouldOmit(item))
    return nextValue.length > 0 ? nextValue : undefined
  }

  if (!isObject(value)) {
    return shouldOmit(value) ? undefined : value
  }

  const nextValue = Object.entries(value).reduce((acc, [key, item]) => {
    const prunedItem = pruneValue(item)
    if (!shouldOmit(prunedItem)) {
      acc[key] = prunedItem
    }
    return acc
  }, {})

  return Object.keys(nextValue).length > 0 ? nextValue : undefined
}

const pruneObject = (value) => pruneValue(value) || {}

const normalizeLevel = (level = 'info') => {
  const normalizedLevel = String(level).toLowerCase()
  return severityByLevel[normalizedLevel] === undefined
    ? 'info'
    : normalizedLevel
}

const getNextSequence = (correlationId) => {
  const sequenceKey = correlationId || ORPHAN_SEQUENCE_KEY
  const nextSequence = (sequenceByScope.get(sequenceKey) || 0) + 1

  sequenceByScope.delete(sequenceKey)
  sequenceByScope.set(sequenceKey, nextSequence)
  if (sequenceByScope.size > SEQUENCE_SCOPE_LIMIT) {
    sequenceByScope.delete(sequenceByScope.keys().next().value)
  }

  return nextSequence
}

const resolveHttpStatus = (status) => {
  if (typeof status !== 'number') return undefined
  if (status >= 200 && status < 400) return 'success'
  if (status >= 400) return 'failed'
  return undefined
}

const normalizeRequest = (request = {}) => ({
  requestId: request.requestId ?? request.request_id,
  traceId: request.traceId ?? request.trace_id ?? request.traceparent,
  organizationId: request.organizationId ?? request.organization_id,
  method: request.method ?? request.http_method,
  url: request.url ?? request.http_path,
  status: request.status ?? request.http_status,
  durationMs: request.durationMs ?? request.duration_ms,
})

const normalizeOperation = (operation = {}, request = {}) => {
  const httpStatus =
    operation.httpStatus ?? operation.http_status ?? request.status
  const status =
    operation.status ??
    operation.operationStatus ??
    operation.operation_status ??
    resolveHttpStatus(httpStatus)

  return pruneObject({
    name: operation.name ?? operation.operationName ?? operation.operation_name,
    duration_ms:
      operation.durationMs ?? operation.duration_ms ?? request.durationMs,
    status,
    retry_count: operation.retryCount ?? operation.retry_count,
    document_id: operation.documentId ?? operation.document_id,
    document_type: operation.documentType ?? operation.document_type,
    processed_count: operation.processedCount ?? operation.processed_count,
    failed_count: operation.failedCount ?? operation.failed_count,
    skipped_count: operation.skippedCount ?? operation.skipped_count,
    http_method:
      operation.httpMethod ?? operation.http_method ?? request.method,
    http_path: operation.httpPath ?? operation.http_path ?? request.url,
    http_status: httpStatus,
  })
}

const buildPayloadBlocks = (envelope) =>
  pruneObject({
    timestamp: envelope.timestamp,
    context: {
      trace_id: envelope.context?.trace_id,
    },
    source: {
      component: envelope.source?.component,
      function: envelope.source?.function,
      file: envelope.source?.file,
    },
    operation: {
      duration_ms: envelope.operation?.duration_ms,
      retry_count: envelope.operation?.retry_count,
      document_id: envelope.operation?.document_id,
      document_type: envelope.operation?.document_type,
      processed_count: envelope.operation?.processed_count,
      failed_count: envelope.operation?.failed_count,
      skipped_count: envelope.operation?.skipped_count,
      http_method: envelope.operation?.http_method,
      http_path: envelope.operation?.http_path,
      http_status: envelope.operation?.http_status,
    },
    error: envelope.error,
    custom_properties: envelope.custom_properties,
  })

const serializePayload = (payloadBlocks) => {
  try {
    const payload = JSON.stringify(payloadBlocks)
    if (payload.length <= PAYLOAD_MAX_LENGTH) {
      return { payload, payloadTruncated: false }
    }

    return {
      payload: JSON.stringify({
        truncated: true,
        original_length: payload.length,
      }),
      payloadTruncated: true,
    }
  } catch {
    return {
      payload: JSON.stringify({ serialization_error: true }),
      payloadTruncated: true,
    }
  }
}

const mergeDefined = (target, values) =>
  Object.entries(values).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key] = value
    }
    return acc
  }, target)

export const buildLogEnvelope = ({
  level = 'info',
  message,
  component,
  functionName,
  file,
  request,
  requestId,
  traceId,
  correlationId,
  organizationId,
  clientName,
  operation,
  error,
  customProperties,
  timestamp = new Date().toISOString(),
} = {}) => {
  const normalizedLevel = normalizeLevel(level)
  const normalizedRequest = normalizeRequest(request)
  const resolvedCorrelationId = correlationId
  const resolvedOrganizationId =
    organizationId ??
    normalizedRequest.organizationId ??
    getRequestOrganizationId()

  return pruneObject({
    version: LOG_SCHEMA_VERSION,
    timestamp,
    level: normalizedLevel,
    message: sanitizeLogString(message || 'Frontend log record'),
    context: {
      correlation_id: resolvedCorrelationId,
      request_id: requestId ?? normalizedRequest.requestId,
      trace_id: traceId ?? normalizedRequest.traceId,
      sequence: getNextSequence(resolvedCorrelationId),
      run_id: LOGGER_RUN_ID,
      organization_id: resolvedOrganizationId,
      client_name: clientName,
    },
    source: {
      application: FRONTEND_SERVICE,
      component: sanitizeLogString(component),
      function: sanitizeLogString(functionName),
      file: sanitizeLogString(file),
    },
    operation: sanitizeLogValue(
      normalizeOperation(operation, normalizedRequest),
    ),
    error: sanitizeErrorDetails(error),
    custom_properties: sanitizeCustomProperties(customProperties),
  })
}

export const buildTraceRecord = (envelope) => {
  const { payload, payloadTruncated } = serializePayload(
    buildPayloadBlocks(envelope),
  )
  const properties = pruneObject({
    version: envelope.version,
    service: envelope.source?.application,
    level: envelope.level,
    correlation_id: envelope.context?.correlation_id,
    request_id: envelope.context?.request_id,
    trace_id: envelope.context?.trace_id,
    run_id: envelope.context?.run_id,
    sequence: envelope.context?.sequence,
    organization_id: envelope.context?.organization_id,
    client_name: envelope.context?.client_name,
    operation_name: envelope.operation?.name,
    operation_status: envelope.operation?.status,
    payload,
    payload_truncated: payloadTruncated || undefined,
  })

  return {
    type: 'trace',
    message: envelope.message,
    severityLevel: severityByLevel[envelope.level],
    properties,
  }
}

export const buildExceptionRecord = (envelope, exception) => {
  const traceRecord = buildTraceRecord(envelope)
  const exceptionText =
    exception.stack || `${exception.name || 'Error'}: ${exception.message}`

  return {
    type: 'exception',
    exception: exceptionText,
    severityLevel: traceRecord.severityLevel,
    properties: traceRecord.properties,
  }
}

export const logTrace = (options = {}) => {
  try {
    const envelope = buildLogEnvelope(options)
    telemetryClient.trackTrace(buildTraceRecord(envelope))
    return envelope
  } catch {
    return undefined
  }
}

export const logError = (options = {}) =>
  logTrace({
    ...options,
    level: 'error',
  })

export const logException = (options = {}) => {
  try {
    const envelope = buildLogEnvelope({
      ...options,
      level: options.level || 'error',
    })

    const exception = createSanitizedException(options.error, envelope.message)

    telemetryClient.trackException(buildExceptionRecord(envelope, exception))

    return envelope
  } catch {
    return undefined
  }
}

export const logOperation = ({
  name,
  status,
  durationMs,
  httpMethod,
  httpPath,
  httpStatus,
  operation,
  ...options
} = {}) =>
  logTrace({
    ...options,
    operation: mergeDefined(
      { ...operation },
      {
        name,
        status,
        durationMs,
        httpMethod,
        httpPath,
        httpStatus,
      },
    ),
  })
